import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import litigesApi from '../../services/litiges';
import litigeDocsApi from '../../services/litige-documents';
import { compressFile, compressPdfClientSide, normalizeDoc } from '../../utils/documents';
import {
  ActionsContact, Bloc, CarteLigne, Feuille, Fiche, Kv, LienOrdinateur, Liste, Pill, Plaque, Puces, Recherche,
  dateFr, messageErreur, normaliser, quand, useFicheOuverte, useToast,
} from './MobileUI';
import './LitigesMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Litiges — version téléphone (2026-10-24).
//
// Le cas d'usage : la personne qui suit les litiges de sa concession regarde
// ce qui est EN RETARD, rappelle le client, note ce qui s'est dit, photographie
// un courrier ou un dommage, et décale la prochaine action. La file est donc
// triée par date de prochaine action (le plus ancien retard en haut), avec les
// puces En retard · Aujourd'hui · À venir · Sans date · Tous.
//
// Mêmes routes et mêmes règles que Litiges.js :
//   - liste : `GET /litiges?tout=1`, comme l'ordinateur. Sans `tout`, le
//     serveur masque les litiges dont l'action est planifiée dans le futur :
//     la puce « À venir » serait toujours vide et un litige décalé à la
//     semaine prochaine disparaîtrait du téléphone ;
//   - dimensions : un appui = `PUT {dimensions}` tout de suite ;
//   - prochaine action : `PUT {date_prochaine_action}` ;
//   - commentaire : `POST /:id/commentaires` avec sa dimension (la route
//     l'accepte et met à jour `dimension_courante` ; l'ordinateur envoie
//     toujours « amiable » faute de sélecteur) ;
//   - documents : PDF par `upload-file` (multipart, compressé dans le
//     navigateur puis par le serveur), le reste par `presign` → PUT S3 →
//     `confirm`, exactement comme la zone de dépôt de l'ordinateur ;
//   - archivage : commentaire « [Clôture] … » puis `POST /:id/archive`, comme
//     `confirmArchive`. Depuis le 2026-10-24 le litige reste en base
//     (`archive_le`), commentaires et documents compris.
// Action principale : « Ajouter un commentaire » — c'est le geste de tous les
// jours (111 commentaires sur 3 mois contre 53 archivages) ; il porte aussi la
// pièce jointe et la date suivante. « Archiver » est en bas de la fiche, hors
// de portée du pouce, derrière une feuille à texte obligatoire.
// Reste sur ordinateur : création complète, import, analyse IA, archives,
// modification complète (coordonnées, véhicule, responsable, concession), les
// 4 dates, renommage et suppression des documents, facture, suppression.
// ═══════════════════════════════════════════════════════════════════════════

// Référentiel des dimensions : mêmes clés, libellés et teintes que DIMENSIONS
// de Litiges.js. light-dark() garde la teinte exacte en clair et l'éclaircit
// en sombre (même formule que utils/theme.js `texteTheme`), sinon « Clôture »
// (#1f2937) disparaît sur une carte sombre.
const DIMENSIONS = [
  { key: 'amiable', label: 'Amiable', abbr: 'A', couleur: 'light-dark(#22c55e, color-mix(in srgb, #22c55e 60%, white))' },
  { key: 'mediation', label: 'Médiation', abbr: 'M', couleur: 'light-dark(#3b82f6, color-mix(in srgb, #3b82f6 60%, white))' },
  { key: 'protection_juridique', label: 'Protection juridique', abbr: 'P', couleur: 'light-dark(#8b5cf6, color-mix(in srgb, #8b5cf6 60%, white))' },
  { key: 'expertise', label: 'Expertise', abbr: 'E', couleur: 'light-dark(#f97316, color-mix(in srgb, #f97316 60%, white))' },
  { key: 'judiciaire', label: 'Judiciaire', abbr: 'J', couleur: 'light-dark(#ef4444, color-mix(in srgb, #ef4444 60%, white))' },
  { key: 'cloture', label: 'Clôture', abbr: 'C', couleur: 'light-dark(#1f2937, color-mix(in srgb, #1f2937 35%, white))' },
];
const dimension = (k) => DIMENSIONS.find((d) => d.key === k);

const FILES = [
  { cle: 'retard', libelle: 'En retard', groupe: 'En retard', alerte: true },
  { cle: 'aujourdhui', libelle: 'Aujourd’hui', groupe: 'Aujourd’hui' },
  { cle: 'avenir', libelle: 'À venir', groupe: 'À venir' },
  { cle: 'sansdate', libelle: 'Sans date', groupe: 'Sans prochaine action' },
];
const RANG = { retard: 0, aujourdhui: 1, avenir: 2, sansdate: 3 };
const LISERE = { retard: 'var(--color-error)', aujourdhui: 'var(--color-warning)', avenir: 'var(--color-accent)', sansdate: 'var(--border-strong)' };
const TYPES_FICHIER = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.msg';

/** « AAAA-MM-JJ » du jour, en heure LOCALE (l'ISO UTC passe au lendemain le soir aux Antilles). */
function aujourdhuiIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const jourDe = (v) => (v ? String(v).split('T')[0] : '');
const ecartJours = (a, b) => Math.round((new Date(`${a}T12:00:00`) - new Date(`${b}T12:00:00`)) / 86400000);

/** Tranche de la prochaine action + libellé court (« en retard de 9 j », « aujourd’hui », « le 12/11 »). */
function echeance(l, auj = aujourdhuiIso()) {
  const j = jourDe(l.date_prochaine_action);
  if (!j) return { file: 'sansdate', libelle: 'sans date', jour: '' };
  // Année saisie sur 2 chiffres (« 0026 ») : on ne calcule pas 730 000 jours de retard.
  if (j < '2000') return { file: 'retard', libelle: 'date à corriger', jour: j };
  if (j < auj) { const n = ecartJours(auj, j); return { file: 'retard', libelle: `en retard de ${n} j`, jour: j }; }
  if (j === auj) return { file: 'aujourdhui', libelle: 'aujourd’hui', jour: j };
  return { file: 'avenir', libelle: `le ${j.slice(8, 10)}/${j.slice(5, 7)}`, jour: j };
}

const nomClient = (l) => [l.nom, l.prenom].filter(Boolean).join(' ').trim() || 'Client sans nom';
const vehicule = (l) => [l.marque, l.modele].filter(Boolean).join(' ');
const responsable = (l) => [l.responsable_prenom, l.responsable_nom].filter(Boolean).join(' ');
const dimsDe = (l) => l.dimensions || l.dimensions_actives || [];
const immatNorm = (s) => String(s || '').toUpperCase().replace(/[\s\-_]/g, '');

/** Envoi d'un document : mêmes routes et même compression que la zone de dépôt de l'ordinateur. */
async function envoyerDocument(litigeId, file, commentaireId) {
  if (file.type === 'application/pdf') {
    const compresse = await compressPdfClientSide(file);
    const fd = new FormData();
    fd.append('file', compresse, compresse.name);
    if (commentaireId) fd.append('commentaire_id', commentaireId);
    const r = await litigeDocsApi.uploadFile(litigeId, fd);
    return normalizeDoc(r.data.document);
  }
  const compresse = await compressFile(file);
  const type = compresse.type || 'application/octet-stream';
  const p = await litigeDocsApi.presign(litigeId, { filename: compresse.name, contentType: type });
  const put = await fetch(p.data.uploadUrl, { method: 'PUT', body: compresse, headers: { 'Content-Type': type } });
  if (!put.ok) throw new Error(`Envoi refusé par le stockage (${put.status})`);
  const c = await litigeDocsApi.confirm(litigeId, {
    key: p.data.key, filename: compresse.name, contentType: type, size: compresse.size, commentaire_id: commentaireId || undefined,
  });
  return normalizeDoc(c.data.document);
}

function Pastilles({ actives }) {
  return (
    <span className="mb-lit-dims" aria-label={`Dimensions : ${DIMENSIONS.filter((d) => actives.includes(d.key)).map((d) => d.label).join(', ') || 'aucune'}`}>
      {DIMENSIONS.map((d) => (
        <i key={d.key} className={actives.includes(d.key) ? 'is-on' : ''} style={{ '--mb-c': d.couleur }} aria-hidden="true">{d.abbr}</i>
      ))}
    </span>
  );
}

export default function LitigesMobile({ user, onVersionOrdinateur, prefill, onPrefillConsumed }) {
  const estOperateur = user?.role === 'operateur';
  const [litiges, setLitiges] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [file, setFile] = useState(null); // choisie au premier chargement
  const [recherche, setRecherche] = useState('');
  const [toast, afficher] = useToast();

  const charger = useCallback(async () => {
    try {
      const r = await litigesApi.getAll({ tout: 1 });
      setLitiges(r.data.litiges || []);
      setErreur('');
    } catch (e) {
      setErreur(messageErreur(e, 'Les litiges n’ont pas pu être chargés.'));
    } finally {
      setChargement(false);
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  // Retour sur l'onglet (après un appel) et chaque minute.
  useEffect(() => {
    const auRetour = () => { if (document.visibilityState === 'visible') charger(); };
    document.addEventListener('visibilitychange', auRetour);
    const minuteur = setInterval(auRetour, 60000);
    return () => { document.removeEventListener('visibilitychange', auRetour); clearInterval(minuteur); };
  }, [charger]);

  const auj = aujourdhuiIso();
  // Concession : même règle que `matchConcession` de l'ordinateur. Le serveur
  // ne filtre pas un super-admin ; la concession choisie dans le sélecteur
  // global arrive dans `user.concession_id` (effectiveUser).
  const concession = user?.concession_id || null;
  const deLaConcession = useMemo(() => (concession ? litiges.filter((l) => l.concession_id === concession) : litiges), [litiges, concession]);
  const avecEcheance = useMemo(() => deLaConcession.map((l) => ({ l, e: echeance(l, auj) })), [deLaConcession, auj]);
  const comptes = useMemo(() => {
    const c = { retard: 0, aujourdhui: 0, avenir: 0, sansdate: 0 };
    avecEcheance.forEach(({ e }) => { c[e.file] += 1; });
    return c;
  }, [avecEcheance]);

  // On arrive sur « En retard » s'il y en a, sinon sur « Tous ».
  useEffect(() => {
    if (file === null && !chargement && !erreur) setFile(comptes.retard ? 'retard' : 'tous');
  }, [file, chargement, erreur, comptes.retard]);
  const fileActive = file || 'tous';

  // Recherche : nom, prénom, téléphone (chiffres seuls), immatriculation sans tirets.
  const q = normaliser(recherche);
  const qChiffres = recherche.replace(/\D/g, '');
  const qImmat = immatNorm(recherche);
  const correspond = useCallback((l) => !q
    || normaliser(`${l.nom || ''} ${l.prenom || ''}`).includes(q)
    || normaliser(`${l.prenom || ''} ${l.nom || ''}`).includes(q)
    || (qChiffres.length >= 3 && String(l.telephone || '').replace(/\D/g, '').includes(qChiffres))
    || (qImmat.length >= 2 && immatNorm(l.immatriculation).includes(qImmat)), [q, qChiffres, qImmat]);

  // Tri : retard (le plus ancien d'abord), aujourd'hui, à venir (le plus proche
  // d'abord), puis sans date (le plus récemment ouvert d'abord). Une recherche
  // porte sur toute la liste, quelle que soit la puce.
  const visibles = useMemo(() => avecEcheance
    .filter(({ l, e }) => (q ? correspond(l) : (fileActive === 'tous' || e.file === fileActive)))
    .sort((a, b) => (RANG[a.e.file] - RANG[b.e.file])
      || (a.e.jour && b.e.jour ? a.e.jour.localeCompare(b.e.jour) : 0)
      || (new Date(b.l.date_ouverture || b.l.created_at) - new Date(a.l.date_ouverture || a.l.created_at)))
    .map(({ l }) => l), [avecEcheance, fileActive, q, correspond]);

  const cle = useCallback((l) => l.id, []);
  const fiche = useFicheOuverte(visibles, cle);

  const majLocale = useCallback((id, champs) => setLitiges((prev) => prev.map((l) => (l.id === id ? { ...l, ...champs } : l))), []);
  const retirer = (id) => {
    if (fiche.suivant) fiche.suivant(); else if (fiche.precedent) fiche.precedent(); else fiche.fermer();
    setLitiges((prev) => prev.filter((l) => l.id !== id));
  };

  // Création depuis le Parking : elle se fait sur ordinateur (marque, modèle,
  // responsable, concession, dimensions). On ne consomme le pré-remplissage
  // qu'au choix de la personne : « Version ordinateur » le laisse intact, et
  // Litiges.js ouvre alors la création déjà remplie.
  // « Ouvrir le litige » : on passe sur « Tous » (le litige peut être dans une
  // autre puce que celle affichée), puis on l'ouvre une fois la liste recalculée.
  const [aOuvrir, setAOuvrir] = useState(null);
  useEffect(() => {
    if (aOuvrir == null) return;
    const i = visibles.findIndex((l) => l.id === aOuvrir);
    if (i >= 0) { fiche.ouvrir(i); setAOuvrir(null); }
  }, [aOuvrir, visibles]); // eslint-disable-line react-hooks/exhaustive-deps
  const dejaEnCours = prefill?.immatriculation
    ? litiges.find((l) => immatNorm(l.immatriculation) === immatNorm(prefill.immatriculation)) : null;

  return (
    <div className="mb-page">
      {prefill && (
        <div className="mb-carton mb-lit-prefill" role="status">
          <b>Nouveau litige depuis le Parking</b>
          <span className="mb-entete-haut"><Plaque immat={prefill.immatriculation} />{prefill.nom || ''}</span>
          {dejaEnCours
            ? <p className="mb-aide">Un litige est déjà en cours pour ce véhicule.</p>
            : <p className="mb-aide">La création d’un litige (véhicule, responsable, dimensions, documents) se fait sur la version ordinateur, déjà pré-remplie.</p>}
          <div className="mb-rangee">
            {dejaEnCours
              ? <button type="button" className="mb-mini mb-mini--accent" onClick={() => { onPrefillConsumed(); setRecherche(''); setFile('tous'); setAOuvrir(dejaEnCours.id); }}>Ouvrir le litige</button>
              : <button type="button" className="mb-mini mb-mini--accent" onClick={onVersionOrdinateur}>Créer sur la version ordinateur</button>}
            <button type="button" className="mb-mini" onClick={onPrefillConsumed}>Ignorer</button>
          </div>
        </div>
      )}

      <div className="mb-outils">
        <Recherche id="mb-lit-q" valeur={recherche} onChange={setRecherche} placeholder="Immat, nom, téléphone" />
      </div>
      {!q && (
        <Puces label="Prochaine action" valeur={fileActive} onChange={setFile} options={[
          ...FILES.map((f) => ({ cle: f.cle, libelle: f.libelle, compte: comptes[f.cle], alerte: f.alerte })),
          { cle: 'tous', libelle: 'Tous', compte: litiges.length },
        ]} />
      )}

      <Liste
        elements={visibles}
        cle={cle}
        chargement={chargement}
        erreur={erreur}
        vide={q ? 'Aucun litige en cours ne correspond.' : fileActive === 'retard' ? 'Aucun litige en retard.' : 'Aucun litige dans cette file.'}
        groupe={(l) => FILES.find((f) => f.cle === echeance(l, auj).file).groupe}
        onOuvrir={fiche.ouvrir}
        carte={(l) => {
          const e = echeance(l, auj);
          return {
            couleur: LISERE[e.file],
            contenu: (
              <>
                <CarteLigne avant={<Plaque immat={l.immatriculation} />} titre={nomClient(l)} age={e.libelle} retard={e.file === 'retard'} />
                <span className="mb-carte-texte">{[vehicule(l), responsable(l) && `Resp. ${responsable(l)}`, !estOperateur && l.concession_nom].filter(Boolean).join(' · ') || '—'}</span>
                <span className="mb-carte-meta">
                  <Pastilles actives={dimsDe(l)} />
                  {l.chez_conseil && <Pill ton="accent">⚖️ Conseil</Pill>}
                  {Number(l.commentaires_count) > 0 && <Pill>{l.commentaires_count} comm.</Pill>}
                </span>
              </>
            ),
          };
        }}
      />
      <LienOrdinateur onClick={onVersionOrdinateur}>Création, import, archives, analyse IA : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FicheLitige
          key={fiche.ouverte.id}
          litige={fiche.ouverte}
          estOperateur={estOperateur}
          position={fiche.index}
          total={visibles.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          onMaj={majLocale}
          onRetire={retirer}
          afficher={afficher}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {toast}
    </div>
  );
}

function FicheLitige({ litige, estOperateur, position, total, onRetour, onPrecedent, onSuivant, onMaj, onRetire, afficher, onVersionOrdinateur }) {
  const [commentaires, setCommentaires] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [envois, setEnvois] = useState([]); // noms des fichiers en cours d'envoi
  const [dims, setDims] = useState(dimsDe(litige));
  const [date, setDate] = useState(jourDe(litige.date_prochaine_action));
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const [feuille, setFeuille] = useState(null); // 'commentaire' | 'archive'
  const photoRef = useRef(null);
  const fichierRef = useRef(null);

  const dateServeur = jourDe(litige.date_prochaine_action);
  const e = echeance(litige);

  const chargerCommentaires = useCallback(() => {
    litigesApi.getById(litige.id).then((r) => setCommentaires(r.data.commentaires || [])).catch(() => setCommentaires([]));
  }, [litige.id]);
  const chargerDocuments = useCallback(() => {
    litigeDocsApi.getAll(litige.id).then((r) => setDocuments((r.data.documents || []).map(normalizeDoc))).catch(() => setDocuments([]));
  }, [litige.id]);
  useEffect(() => { chargerCommentaires(); chargerDocuments(); }, [chargerCommentaires, chargerDocuments]);

  // Un appui = enregistrement immédiat, comme l'ordinateur. Seules les
  // dimensions sont envoyées : depuis le 2026-10-24, `PUT /litiges/:id`
  // n'écrit la prochaine action que si elle est fournie.
  const basculer = async (k) => {
    if (occupe) return;
    const avant = dims;
    const apres = dims.includes(k) ? dims.filter((d) => d !== k) : [...dims, k];
    setDims(apres); setOccupe(true); setErreur('');
    try {
      await litigesApi.update(litige.id, { dimensions: apres });
      onMaj(litige.id, { dimensions: apres, dimensions_actives: apres });
      afficher(`${dimension(k).label} : ${apres.includes(k) ? 'activée' : 'retirée'}`);
    } catch (err) {
      setDims(avant);
      setErreur(messageErreur(err, 'La dimension n’a pas pu être enregistrée.'));
    } finally {
      setOccupe(false);
    }
  };

  const enregistrerDate = async (v) => {
    setOccupe(true); setErreur('');
    try {
      await litigesApi.update(litige.id, { date_prochaine_action: v || null });
      onMaj(litige.id, { date_prochaine_action: v || null });
      setDate(v || '');
      afficher(v ? `Prochaine action le ${dateFr(v)}` : 'Prochaine action effacée');
    } catch (err) {
      setErreur(messageErreur(err, 'La date n’a pas pu être enregistrée.'));
    } finally {
      setOccupe(false);
    }
  };

  const joindre = async (fichiers) => {
    const liste = Array.from(fichiers || []);
    if (!liste.length) return;
    setErreur('');
    const rates = [];
    for (const f of liste) {
      setEnvois((p) => [...p, f.name]);
      try {
        const doc = await envoyerDocument(litige.id, f);
        setDocuments((p) => [...(p || []), doc]);
      } catch (err) {
        rates.push(f.name);
      } finally {
        setEnvois((p) => p.filter((n) => n !== f.name));
      }
    }
    if (rates.length) setErreur(`Non envoyé : ${rates.join(', ')}. Réessayez, ou ajoutez-le depuis l’ordinateur.`);
    else afficher(liste.length > 1 ? `${liste.length} documents ajoutés` : 'Document ajouté');
  };

  const docsDuCommentaire = (id) => (documents || []).filter((d) => d.commentaire_id === id);
  const dernier = commentaires && commentaires[0];

  return (
    <Fiche
      titre={`Litige #${litige.id}`}
      position={position}
      total={total}
      onRetour={onRetour}
      onPrecedent={onPrecedent}
      onSuivant={onSuivant}
      pied={<button type="button" className="mb-bouton mb-bouton--principal" onClick={() => setFeuille('commentaire')}>Ajouter un commentaire</button>}
    >
      <div className="mb-entete">
        <div className="mb-entete-haut"><Plaque immat={litige.immatriculation} />{vehicule(litige)}</div>
        <div>
          <div className="mb-entete-titre">{nomClient(litige)}</div>
          <div className="mb-entete-sous">
            {[responsable(litige) && `Responsable : ${responsable(litige)}`, litige.date_ouverture && `ouvert le ${dateFr(jourDe(litige.date_ouverture))}`].filter(Boolean).join(' · ')}
          </div>
        </div>
        <ActionsContact tel={litige.telephone} email={litige.email || ''} />
        {(litige.telephone || litige.email) && <p className="mb-aide">{[litige.telephone, litige.email].filter(Boolean).join(' · ')}</p>}
        {litige.chez_conseil && <span><Pill ton="accent">⚖️ Transmis à notre conseil</Pill></span>}
      </div>

      <div className={`mb-carton mb-lit-echeance is-${e.file}`}>
        <b>Prochaine action <span className="mb-lit-echeance-etat">{e.file === 'sansdate' ? 'aucune' : e.libelle}</span></b>
        <div className="mb-saisie">
          <input type="date" aria-label="Date de prochaine action" value={date} onChange={(ev) => setDate(ev.target.value)} />
          <button type="button" disabled={occupe || date === dateServeur} onClick={() => enregistrerDate(date)}>Enregistrer</button>
        </div>
        {dateServeur && (
          <div className="mb-rangee">
            <button type="button" className="mb-mini" disabled={occupe} onClick={() => enregistrerDate('')}>Effacer la date</button>
          </div>
        )}
      </div>

      <div className="mb-carton">
        <b>Dimensions</b>
        <div className="mb-grille">
          {DIMENSIONS.map((d) => (
            <button key={d.key} type="button" className="mb-lit-dimbtn" aria-pressed={dims.includes(d.key)}
              style={{ '--mb-c': d.couleur }} disabled={occupe} onClick={() => basculer(d.key)}>
              <i aria-hidden="true">{d.abbr}</i>{d.label}
            </button>
          ))}
        </div>
        {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      </div>

      <Bloc titre="Documents" compte={documents ? documents.length : null}
        resume={envois.length ? 'envoi…' : documents && documents.length ? `dernier : ${documents[documents.length - 1].name || ''}` : 'aucun'}>
        {documents === null && <p className="mb-aide">Chargement…</p>}
        {(documents?.length > 0 || envois.length > 0) && (
          <div className="mb-lit-docs">
            {[...(documents || [])].reverse().map((d) => <VignetteDoc key={d.id} doc={d} />)}
            {envois.map((n) => <span key={n} className="mb-lit-doc is-envoi"><span className="mb-lit-doc-ic">Envoi…</span><small>{n}</small></span>)}
          </div>
        )}
        <div className="mb-rangee">
          <button type="button" className="mb-mini mb-mini--accent" onClick={() => photoRef.current?.click()}>📷 Photo</button>
          <button type="button" className="mb-mini" onClick={() => fichierRef.current?.click()}>📎 Fichier</button>
        </div>
        <input ref={photoRef} type="file" accept="image/*" capture="environment" hidden
          onChange={(ev) => { joindre(ev.target.files); ev.target.value = ''; }} />
        <input ref={fichierRef} type="file" accept={TYPES_FICHIER} multiple hidden
          onChange={(ev) => { joindre(ev.target.files); ev.target.value = ''; }} />
        <p className="mb-aide">Renommer ou supprimer un document : version ordinateur.</p>
      </Bloc>

      <Bloc titre="Commentaires" compte={commentaires ? commentaires.length : null}
        resume={dernier ? quand(dernier.created_at) : ''} ouvert>
        {commentaires === null && <p className="mb-aide">Chargement…</p>}
        {commentaires && commentaires.length === 0 && <p className="mb-aide">Aucun commentaire.</p>}
        {(commentaires || []).map((c) => {
          const d = dimension(c.dimension);
          const lies = docsDuCommentaire(c.id);
          return (
            <div key={c.id} className="mb-commentaire">
              {c.commentaire}
              {lies.map((doc) => (
                <a key={doc.id} className="mb-lit-lien" href={doc.url} target="_blank" rel="noopener noreferrer">📎 {doc.name}</a>
              ))}
              <small>
                {d && <span className="mb-lit-badge" style={{ '--mb-c': d.couleur }}>{d.label}</span>}
                {[c.user_prenom, c.user_nom].filter(Boolean).join(' ') || '—'} · {quand(c.created_at)}
              </small>
            </div>
          );
        })}
      </Bloc>

      {!estOperateur && litige.concession_nom && <Kv lignes={[['Concession', litige.concession_nom]]} />}

      <button type="button" className="mb-bouton mb-lit-archiver" onClick={() => setFeuille('archive')}>Archiver ce litige…</button>
      <p className="mb-aide mb-aide--centre">Modifier le client ou le véhicule, les dates de réception, de 1er contact et de clôture : version ordinateur.</p>
      <LienOrdinateur onClick={onVersionOrdinateur} />

      {feuille === 'commentaire' && (
        <FeuilleCommentaire litige={litige} dimensionDefaut={litige.dimension_courante || null} dateActuelle={dateServeur}
          onFermer={() => setFeuille(null)}
          onFait={({ date: nouvelleDate, dimension: dim, docs }) => {
            setFeuille(null);
            if (nouvelleDate !== undefined) setDate(nouvelleDate || '');
            onMaj(litige.id, {
              commentaires_count: Number(litige.commentaires_count || 0) + 1,
              ...(dim ? { dimension_courante: dim } : {}),
              ...(nouvelleDate !== undefined ? { date_prochaine_action: nouvelleDate || null } : {}),
            });
            if (docs.length) setDocuments((p) => [...(p || []), ...docs]);
            chargerCommentaires();
            afficher(nouvelleDate ? `Commentaire ajouté · prochaine action le ${dateFr(nouvelleDate)}` : 'Commentaire ajouté');
          }} />
      )}

      {feuille === 'archive' && (
        <FeuilleArchive litige={litige} onFermer={() => setFeuille(null)}
          onFait={() => { setFeuille(null); afficher('Litige archivé'); onRetire(litige.id); }} />
      )}
    </Fiche>
  );
}

function VignetteDoc({ doc }) {
  const type = doc.type || '';
  const image = type.startsWith('image/');
  const libelle = type === 'application/pdf' ? 'PDF' : /\.msg$/i.test(doc.name || '') ? 'E-MAIL' : 'DOC';
  // Ouverture dans un nouvel onglet : le lecteur du téléphone (PDF, image en
  // plein écran, zoom au doigt) fait mieux qu'une visionneuse intégrée.
  return (
    <a className="mb-lit-doc" href={doc.url} target="_blank" rel="noopener noreferrer">
      {image ? <img src={doc.url} alt="" loading="lazy" /> : <span className="mb-lit-doc-ic">{libelle}</span>}
      <small>{doc.name}</small>
    </a>
  );
}

function FeuilleCommentaire({ litige, dimensionDefaut, dateActuelle, onFermer, onFait }) {
  const [texte, setTexte] = useState('');
  const [dim, setDim] = useState(dimensionDefaut);
  const [piece, setPiece] = useState(null);
  const [date, setDate] = useState(dateActuelle || '');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const photoRef = useRef(null);
  const fichierRef = useRef(null);

  const valider = async () => {
    const t = texte.trim();
    if (!t || envoi) return;
    setEnvoi(true); setErreur('');
    let etape = 'Le commentaire n’a pas pu être enregistré.';
    try {
      const r = await litigesApi.addCommentaire(litige.id, t, dim);
      const docs = [];
      if (piece) {
        etape = 'Commentaire enregistré, mais la pièce jointe n’a pas pu être envoyée.';
        docs.push(await envoyerDocument(litige.id, piece, r.data.commentaire?.id));
      }
      let nouvelleDate;
      if (date !== (dateActuelle || '')) {
        etape = 'Commentaire enregistré, mais la date n’a pas pu être changée.';
        await litigesApi.update(litige.id, { date_prochaine_action: date || null });
        nouvelleDate = date;
      }
      onFait({ date: nouvelleDate, dimension: dim, docs });
    } catch (err) {
      setErreur(etape === 'Le commentaire n’a pas pu être enregistré.' ? messageErreur(err, etape) : etape);
      setEnvoi(false);
    }
  };

  return (
    <Feuille titre="Nouveau commentaire" onFermer={onFermer} fermer="Annuler">
      <div className="mb-champ">
        <label className="mb-etiquette" htmlFor="mb-lit-com">Commentaire</label>
        <textarea id="mb-lit-com" className="mb-zone" value={texte} onChange={(e) => setTexte(e.target.value)}
          placeholder="Ex. : assureur relancé par téléphone, réponse promise sous 8 jours." />
      </div>
      <div className="mb-champ">
        <span className="mb-etiquette">Dimension du commentaire</span>
        <div className="mb-lit-choixdim">
          <button type="button" aria-pressed={!dim} onClick={() => setDim(null)}>Aucune</button>
          {DIMENSIONS.map((d) => (
            <button key={d.key} type="button" aria-pressed={dim === d.key} style={{ '--mb-c': d.couleur }} onClick={() => setDim(d.key)}>
              <i aria-hidden="true" />{d.label}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-champ">
        <span className="mb-etiquette">Pièce jointe (facultatif)</span>
        {piece
          ? <div className="mb-rangee"><span className="mb-lit-piece">📎 {piece.name}</span><button type="button" className="mb-mini" onClick={() => setPiece(null)}>Retirer</button></div>
          : (
            <div className="mb-rangee">
              <button type="button" className="mb-mini" onClick={() => photoRef.current?.click()}>📷 Joindre une photo</button>
              <button type="button" className="mb-mini" onClick={() => fichierRef.current?.click()}>📎 Joindre un fichier</button>
            </div>
          )}
        <input ref={photoRef} type="file" accept="image/*" capture="environment" hidden
          onChange={(e) => { setPiece(e.target.files?.[0] || null); e.target.value = ''; }} />
        <input ref={fichierRef} type="file" accept={TYPES_FICHIER} hidden
          onChange={(e) => { setPiece(e.target.files?.[0] || null); e.target.value = ''; }} />
      </div>
      <div className="mb-champ">
        <label className="mb-etiquette" htmlFor="mb-lit-next">Prochaine action (facultatif)</label>
        <input id="mb-lit-next" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      <div className="mb-feuille-pied">
        <button type="button" className="mb-bouton" onClick={onFermer}>Annuler</button>
        <button type="button" className="mb-bouton mb-bouton--principal" disabled={!texte.trim() || envoi} onClick={valider}>
          {envoi ? 'Envoi…' : 'Enregistrer'}
        </button>
      </div>
    </Feuille>
  );
}

function FeuilleArchive({ litige, onFermer, onFait }) {
  const [texte, setTexte] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const valider = async () => {
    const t = texte.trim();
    if (!t || envoi) return;
    setEnvoi(true); setErreur('');
    try {
      // Même enchaînement que confirmArchive (Litiges.js).
      await litigesApi.addCommentaire(litige.id, `[Clôture] ${t}`);
      await litigesApi.archive(litige.id);
      onFait();
    } catch (err) {
      setErreur(messageErreur(err, 'L’archivage n’a pas abouti.'));
      setEnvoi(false);
    }
  };
  return (
    <Feuille titre="Archiver le litige" onFermer={onFermer} fermer="Annuler">
      <div className="mb-champ">
        <label className="mb-etiquette" htmlFor="mb-lit-cloture">Comment ce litige s’est-il soldé ?</label>
        <textarea id="mb-lit-cloture" className="mb-zone" value={texte} onChange={(e) => setTexte(e.target.value)}
          placeholder="Ex. : geste commercial accepté par le client, courrier de désistement reçu." />
      </div>
      <p className="mb-aide">Obligatoire. Le litige sort de la liste ; ses commentaires et documents restent consultables depuis l’ordinateur (onglet Archives).</p>
      {erreur && <p className="mb-aide mb-aide--erreur">{erreur}</p>}
      <div className="mb-feuille-pied">
        <button type="button" className="mb-bouton" onClick={onFermer}>Annuler</button>
        <button type="button" className="mb-bouton mb-bouton--principal" disabled={!texte.trim() || envoi} onClick={valider}>
          {envoi ? 'Archivage…' : 'Archiver'}
        </button>
      </div>
    </Feuille>
  );
}
